"use client"

import { useState } from "react"
import { submitContactEnquiry } from "@lib/data/contact"

const inputClassName =
  "w-full rounded-[10px] border border-[#e8ebf0] px-4 py-3 text-sm text-aov-navy placeholder:text-[#9ca3af] focus:border-aov-navy focus:outline-none focus:ring-2 focus:ring-aov-navy/10 transition-colors"

type Status = "idle" | "submitting" | "success" | "error"

export default function ContactForm() {
  const [status, setStatus] = useState<Status>("idle")

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (status === "submitting") return

    const form = e.currentTarget
    const data = new FormData(form)

    setStatus("submitting")
    try {
      await submitContactEnquiry({
        name: String(data.get("name") ?? "").trim(),
        email: String(data.get("email") ?? "").trim(),
        phone: String(data.get("phone") ?? "").trim() || undefined,
        subject: String(data.get("subject") ?? "").trim() || undefined,
        message: String(data.get("message") ?? "").trim(),
        website: String(data.get("website") ?? ""),
      })
      form.reset()
      setStatus("success")
    } catch {
      setStatus("error")
    }
  }

  if (status === "success") {
    return (
      <div className="rounded-[10px] bg-[#f0faf4] border border-[#c6ecd4] p-6 text-center">
        <h3 className="font-display text-xl font-bold text-aov-navy mb-2">
          Message sent
        </h3>
        <p className="text-sm text-[#6b7280] mb-5">
          Thanks for getting in touch — we&apos;ll get back to you within one
          business day.
        </p>
        <button
          type="button"
          onClick={() => setStatus("idle")}
          className="text-sm font-bold text-aov-red hover:underline"
        >
          Send another message
        </button>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <div>
          <label className="block text-sm font-semibold text-aov-navy mb-1.5">
            Name
          </label>
          <input
            type="text"
            name="name"
            required
            maxLength={200}
            placeholder="Your name"
            className={inputClassName}
          />
        </div>
        <div>
          <label className="block text-sm font-semibold text-aov-navy mb-1.5">
            Email
          </label>
          <input
            type="email"
            name="email"
            required
            maxLength={320}
            placeholder="your@email.com"
            className={inputClassName}
          />
        </div>
      </div>
      <div>
        <label className="block text-sm font-semibold text-aov-navy mb-1.5">
          Phone
        </label>
        <input
          type="tel"
          name="phone"
          maxLength={50}
          placeholder="Your phone number"
          className={inputClassName}
        />
      </div>
      <div>
        <label className="block text-sm font-semibold text-aov-navy mb-1.5">
          Subject
        </label>
        <select
          name="subject"
          className="w-full rounded-[10px] border border-[#e8ebf0] px-4 py-3 text-sm text-aov-navy focus:border-aov-navy focus:outline-none focus:ring-2 focus:ring-aov-navy/10 transition-colors bg-white"
        >
          <option value="">Select a subject</option>
          <option value="General Enquiry">General Enquiry</option>
          <option value="Product Quote">Product Quote</option>
          <option value="Technical Support">Technical Support</option>
          <option value="Trade Account">Trade Account</option>
          <option value="Bespoke Specification">Bespoke Specification</option>
        </select>
      </div>
      <div>
        <label className="block text-sm font-semibold text-aov-navy mb-1.5">
          Message
        </label>
        <textarea
          name="message"
          required
          maxLength={5000}
          rows={5}
          placeholder="Tell us about your project or enquiry..."
          className={`${inputClassName} resize-none`}
        />
      </div>
      {/* Honeypot — invisible to humans; bots that fill it are dropped
          server-side. */}
      <div className="absolute left-[-9999px] top-auto h-px w-px overflow-hidden" aria-hidden="true">
        <label>
          Website
          <input type="text" name="website" tabIndex={-1} autoComplete="off" />
        </label>
      </div>
      {status === "error" && (
        <p className="text-sm font-semibold text-aov-red" role="alert">
          Sorry, something went wrong sending your message. Please try again,
          or email us directly at sales@aov-direct.co.uk.
        </p>
      )}
      <button
        type="submit"
        disabled={status === "submitting"}
        className="w-full rounded-[10px] bg-aov-red px-6 py-3.5 text-sm font-bold text-white transition-all duration-300 hover:bg-[#ff3333] hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(228,39,39,0.3)] disabled:opacity-60 disabled:hover:translate-y-0 disabled:hover:shadow-none disabled:cursor-not-allowed"
      >
        {status === "submitting" ? "Sending..." : "Send Enquiry"}
      </button>
    </form>
  )
}
